import { createContext, useCallback, useState } from "react"
import { useTypedFeatureFlag } from "@/hooks/use-feature-flag"
import { useNavigate } from "react-router-dom"

import { useBillingStore } from "../store/billing-store"
import { SubscriptionLockdownModal } from "./subscription-lockdown-modal"

type LockdownReason = "trial_expired" | "subscription_expired" | null

interface SubscriptionGuardContextValue {
  isLocked: boolean
  lockdownReason: LockdownReason
  guardAction: (action: () => void) => void
}

export const SubscriptionGuardContext = createContext<SubscriptionGuardContextValue | null>(null)

export function SubscriptionGuardProvider({ children }: { children: React.ReactNode }) {
  const { isSubscriptionExpired, isSubscriber } = useBillingStore()
  const lockdownEnabled = useTypedFeatureFlag("subscription_lockdown_enabled", false)
  const navigate = useNavigate()
  const [modalOpen, setModalOpen] = useState(false)

  const lockdownReason: LockdownReason = (() => {
    if (!lockdownEnabled) return null
    if (isSubscriptionExpired === null) return null
    if (!isSubscriptionExpired) return null
    return isSubscriber ? "subscription_expired" : "trial_expired"
  })()

  const isLocked = lockdownReason !== null

  const guardAction = useCallback(
    (action: () => void) => {
      if (!isLocked) {
        action()
        return
      }
      if (lockdownReason === "trial_expired") {
        setModalOpen(true)
      } else if (lockdownReason === "subscription_expired") {
        navigate("/settings/billing")
      }
    },
    [isLocked, lockdownReason, navigate]
  )

  return (
    <SubscriptionGuardContext.Provider value={{ isLocked, lockdownReason, guardAction }}>
      {children}
      <SubscriptionLockdownModal open={modalOpen} onOpenChange={setModalOpen} />
    </SubscriptionGuardContext.Provider>
  )
}
